import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, Link } from 'react-router-dom';
import { SG_FETCH_ACTION } from 'constants/actions';

export default function ActionDetailsPage() {
  const { id } = useParams();
  const dispatch = useDispatch();
  const details = useSelector((s) => s.action_actions?.[id] || null);
  const [copied, setCopied] = useState(false);
console.log('ActionDetailsPage render', { id, details });
  useEffect(() => {
    // defensive: do not dispatch when id is missing or an accidental 'undefined' string
    // The saga expects payload to be an object { id } (see FETCH_APV2_ACTION signature)
    if (id && id !== 'undefined') dispatch({ type: SG_FETCH_ACTION, payload: { id } });
  }, [dispatch, id]);

  if (!id || id === 'undefined') return (
    <div style={{ padding: 12, border: '1px solid #eee', borderRadius: 6, background: '#fff' }}>
      <div style={{ color: '#b91c1c' }}>Invalid action id — nothing to show.</div>
      <div style={{ marginTop: 8 }}><Link to="..">Back to actions</Link></div>
    </div>
  );

  if (!details) {
    return (
      <div style={{ padding: 12, border: '1px dashed #eee', borderRadius: 6, background: '#fff' }}>
        <div style={{ color: '#6b7280' }}>Loading details…</div>
        <div style={{ marginTop: 8 }}>
          <Link to="..">Back</Link>
        </div>
      </div>
    );
  }

  const copyJson = () => {
    try {
      const txt = JSON.stringify(details, null, 2);
      if (navigator && navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(txt).then(() => setCopied(true));
      } else {
        const ta = document.createElement('textarea');
        ta.value = txt;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
        setCopied(true);
      }
      setTimeout(() => setCopied(false), 1500);
    } catch (err) {
      // ignore
    }
  };

  const fmtDateTime = (d) => (d ? new Date(d).toLocaleString() : '-');

  return (
    <div style={{ padding: 12, border: '1px solid #eee', borderRadius: 6, background: '#fff' }}>
      {/* Developer Notes */}
      <div style={{ background: '#fffbeb', border: '1px solid #fbbf24', borderRadius: 6, padding: 12, marginBottom: 16 }}>
        <div style={{ fontWeight: 700, color: '#92400e', marginBottom: 8 }}>🔍 Developer Notes — Action Status Analysis</div>
        <div style={{ color: '#78350f', fontSize: 13, lineHeight: '1.6' }}>
          <div style={{ marginBottom: 6 }}><strong>Status:</strong> {details.status_name || details.status} | <strong>Priority:</strong> {details.priority} | <strong>Progress:</strong> {details.progress_percentage ?? '-'}%</div>
          {details.progress_percentage === 0 && details.created_at && new Date() > new Date(details.created_at).getTime() + 7 * 24 * 60 * 60 * 1000 && <div style={{ color: '#dc2626' }}>⚠️ Action created &gt;7 days ago with 0% progress — no work started</div>}
          {(details.progress_analytics?.last_update_days_ago ?? 999) > 14 && details.status_name !== 'Completed' && <div style={{ color: '#dc2626' }}>⚠️ No updates in {details.progress_analytics?.last_update_days_ago} days — action may be stale</div>}
          {details.progress_percentage < 50 && details.time_bound_deadline && new Date(details.time_bound_deadline) < new Date().getTime() + 7 * 24 * 60 * 60 * 1000 && <div style={{ color: '#dc2626' }}>⚠️ Less than 50% progress with deadline in &lt;7 days — action at risk</div>}
          {details.progress_percentage === 100 && details.status_name !== 'Completed' && <div style={{ color: '#d97706' }}>⚠️ Progress at 100% but status not marked as Completed — update status</div>}
          {details.status_name === 'Completed' && details.progress_percentage !== 100 && <div style={{ color: '#d97706' }}>⚠️ Status is Completed but progress is {details.progress_percentage}% — inconsistent state</div>}
          {details.time_bound_deadline && new Date(details.time_bound_deadline) < new Date() && details.status_name !== 'Completed' && details.status_name !== 'Cancelled' && <div style={{ color: '#dc2626' }}>⚠️ Deadline passed ({fmtDateTime(details.time_bound_deadline)}) but not completed/cancelled — missed deadline</div>}
          {(details.progress_analytics?.total_updates ?? details.recent_updates_count ?? 0) === 0 && <div style={{ color: '#d97706' }}>⚠️ No progress updates recorded — manager not engaged</div>}
        </div>
        <details style={{ marginTop: 12 }}>
          <summary style={{ cursor: 'pointer', color: '#92400e' }}>📖 View validation guide</summary>
          <div style={{ marginTop: 8, padding: 12, background: '#fff', borderRadius: 6, fontSize: 12, fontFamily: 'monospace' }}>
            <div><strong>Expected Status Flow:</strong></div>
            <div>Draft → Active → In Progress → On Track / At Risk → Completed / Cancelled</div>
            <div style={{ marginTop: 8 }}><strong>⚠️ CRITICAL: Status is FK object, not string!</strong></div>
            <div style={{ marginTop: 4, color: '#dc2626' }}>Backend: action.status = ActionStatusList.objects.get(name='Completed')</div>
            <div style={{ marginTop: 8 }}><strong>Red Flags:</strong></div>
            <ul style={{ margin: '6px 0', paddingLeft: 20 }}>
              <li>progress_percentage=0 and status='Completed' → inconsistent</li>
              <li>is_overdue=true but status='On Track' → status not updated</li>
              <li>days_until_deadline &lt; 0 but status != 'Cancelled' → missed</li>
              <li>updates_count=0 and created &gt;7 days ago → no progress</li>
            </ul>
          </div>
        </details>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16 }}>
        <div style={{ minWidth: 0 }}>
          <h2 style={{ margin: 0, fontSize: 18 }}>{details.title || details.name || `Action ${id}`}</h2>
          <div style={{ color: '#6b7280', fontSize: 12, marginTop: 6 }}>ID: <span style={{ fontFamily: 'monospace' }}>{id}</span></div>
          <div style={{ marginTop: 8, color: '#374151' }}>{details.description || <span style={{ color: '#9ca3af' }}>No description</span>}</div>

          <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
            <div style={{ padding: '6px 10px', background: '#f3f4f6', borderRadius: 6 }}>Priority: <strong style={{ marginLeft: 6 }}>{details.priority || '-'}</strong></div>
            <div style={{ padding: '6px 10px', background: '#f3f4f6', borderRadius: 6 }}>Status: <strong style={{ marginLeft: 6 }}>{details.status_name || details.status || '-'}</strong></div>
            <div style={{ padding: '6px 10px', background: '#f3f4f6', borderRadius: 6 }}>Metric focus: <strong style={{ marginLeft: 6 }}>{details.metric_focus || '-'}</strong></div>
          </div>

          <div style={{ marginTop: 12, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div style={{ background: '#fff' }}>
              <h4 style={{ margin: '6px 0 8px 0' }}>Ownership</h4>
              <div style={{ color: '#374151' }}><strong>Manager:</strong> {details.manager?.first_name ? `${details.manager.first_name} ${details.manager.last_name || ''}` : '-'}</div>
              <div style={{ color: '#6b7280', marginTop: 6 }}><strong>Manager email:</strong> {details.manager?.email ?? '-'}</div>
              <div style={{ marginTop: 8 }}><strong>Organization:</strong> {details.organization?.name ?? '-' } <span style={{ color: '#9ca3af', marginLeft: 8 }}>• Org id: {details.organization?.id ?? '-'}</span></div>
            </div>

            <div>
              <h4 style={{ margin: '6px 0 8px 0' }}>Timing & progress</h4>
              <div><strong>Deadline:</strong> {details.time_bound_deadline ? fmtDateTime(details.time_bound_deadline) : '-'}</div>
              <div style={{ marginTop: 6 }}><strong>Progress:</strong> {details.progress_percentage ?? '-'}% — <strong>Last update:</strong> {fmtDateTime(details.last_progress_update)}</div>
              <div style={{ marginTop: 6 }}><strong>Next milestone:</strong> {details.next_milestone || '-'} {details.next_milestone_date ? `on ${fmtDateTime(details.next_milestone_date)}` : ''}</div>
            </div>
          </div>

          <div style={{ marginTop: 12 }}>
            <h4 style={{ margin: '6px 0 8px 0' }}>Campaign</h4>
            <div style={{ color: '#374151' }}>{details.campaign?.name ?? details.campaign_name ?? '—' } <span style={{ color: '#9ca3af', marginLeft: 8 }}>• id: {details.campaign?.id ?? '-'}</span></div>
            <div style={{ marginTop: 6, color: '#6b7280' }}>Dates: {details.campaign?.start_date ? fmtDateTime(details.campaign.start_date) : '-'} → {details.campaign?.end_date ? fmtDateTime(details.campaign.end_date) : '-'}</div>
          </div>
        </div>

        <div style={{ minWidth: 260, maxWidth: 360, display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ padding: 10, borderRadius: 6, background: '#f8fafc', border: '1px solid #eef2ff' }}>
            <div style={{ color: '#374151', fontWeight: 700 }}>Analytics</div>
            <div style={{ color: '#6b7280', marginTop: 6 }}>Updates: {details.progress_analytics?.total_updates ?? details.recent_updates_count ?? 0}</div>
            <div style={{ color: '#6b7280', fontSize: 13, marginTop: 6 }}>Recent updates this week: {details.progress_analytics?.recent_updates_this_week ?? 0}</div>
            <div style={{ color: '#6b7280', fontSize: 13, marginTop: 6 }}>Days since last update: {details.progress_analytics?.last_update_days_ago ?? '-'}</div>
          </div>

          <div style={{ padding: 10, borderRadius: 6, background: '#fff', border: '1px solid #eee' }}>
            <div style={{ fontSize: 13, color: '#374151', fontWeight: 700 }}>Metadata</div>
            <div style={{ color: '#6b7280', marginTop: 8 }}>Created: {fmtDateTime(details.created_at)}</div>
            <div style={{ color: '#6b7280', marginTop: 6 }}>Updated: {fmtDateTime(details.updated_at)}</div>
            <div style={{ color: '#6b7280', marginTop: 6 }}>Tags: {Array.isArray(details.tags) && details.tags.length ? details.tags.join(', ') : '-'}</div>
            <div style={{ color: '#6b7280', marginTop: 6 }}>Client token: {details.custom_fields?.client_token ?? '-'}</div>
          </div>

          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button onClick={copyJson} style={{ padding: '6px 8px', borderRadius: 6 }}>{copied ? 'Copied' : 'Copy JSON'}</button>
            <Link to=".." style={{ padding: '6px 8px', borderRadius: 6 }}>Back</Link>
          </div>
        </div>
      </div>

      <div style={{ marginTop: 14 }}>
        <h4 style={{ marginTop: 0 }}>Latest update</h4>
        <div style={{ color: '#374151' }}>{details.latest_update_summary || '-'}</div>
        <div style={{ marginTop: 8, color: '#6b7280' }}>Check-in summary: {Object.keys(details.check_in_summary || {}).length ? JSON.stringify(details.check_in_summary) : '-'}</div>
      </div>

      <pre style={{ whiteSpace: 'pre-wrap', marginTop: 12, padding: 10, background: '#f8fafc', borderRadius: 6, overflowX: 'auto' }}>{JSON.stringify(details, null, 2)}</pre>
    </div>
  );
}
